import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatEUR, formatDate } from "@/lib/constants";
import { toast } from "sonner";
import { Send, Calendar, Plus, ChevronRight, Loader2, AlertCircle, PauseCircle, PlayCircle } from "lucide-react";

export default function Followups() {
  const [list, setList] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const nav = useNavigate();

  const load = useCallback(async () => {
    const [fRes, oRes] = await Promise.all([api.get("/followups"), api.get("/offers")]);
    setList(fRes.data);
    setOffers(oRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Eligible: offer status "Versendet" AND no followup yet for that lead
  const eligibleOffers = useMemo(() => {
    const leadIdsWithFup = new Set(list.map((f) => f.lead_id));
    return offers.filter((o) => o.status === "Versendet" && !leadIdsWithFup.has(o.lead_id));
  }, [offers, list]);

  const startSequence = async (leadId) => {
    setBusy(leadId);
    try {
      await api.post(`/leads/${leadId}/start-followup`);
      toast.success("Nachfass-Sequenz gestartet");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setBusy("");
    }
  };

  const send = async (id) => {
    try { await api.post(`/followups/${id}/send`); toast.success("Versendet"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const togglePause = async (leadId, currentlyPaused) => {
    try {
      const url = currentlyPaused ? `/leads/${leadId}/resume-followup` : `/leads/${leadId}/pause-followup`;
      await api.post(url);
      toast.success(currentlyPaused ? "Nachfassung fortgesetzt" : "Nachfassung pausiert");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="followups-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Nachfassung</h1>
          <p className="mt-1 text-slate-600">Geplante und versendete Nachfass-Nachrichten</p>
        </div>
        <Button data-testid="new-followup-btn" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Nachfass-Sequenz starten
        </Button>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr className="text-left">
                <th className="px-6 py-3 font-semibold">Kunde</th>
                <th className="px-6 py-3 font-semibold text-right">Angebotswert</th>
                <th className="px-6 py-3 font-semibold">Tag</th>
                <th className="px-6 py-3 font-semibold">Geplant für</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-slate-500">Wird geladen …</td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-16 text-center">
                  <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">Keine aktiven Nachfass-Sequenzen.</p>
                  <Button data-testid="empty-start-followup" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Erste Sequenz starten
                  </Button>
                </td></tr>
              ) : list.map((f) => {
                const isPaused = f.status === "paused" || f.paused || f.lead_paused;
                const isSent = f.status === "sent";
                return (
                <tr key={f.followup_id} className={`hover:bg-slate-50 ${isPaused && !isSent ? "bg-amber-50/40" : ""}`}>
                  <td className="px-6 py-4 font-medium text-slate-900">
                    <button data-testid={`fup-customer-${f.followup_id}`} onClick={() => nav(`/leads/${f.lead_id}`)} className="hover:text-orange-600">
                      {f.customer_name}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums">{formatEUR(f.offer_value)}</td>
                  <td className="px-6 py-4">Tag {f.day}: {f.subject}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{formatDate(f.scheduled_for)}</td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isSent ? "bg-emerald-50 text-emerald-700" : isPaused ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                      {isSent ? "Versendet" : isPaused ? "Pausiert" : "Geplant"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!isSent && !isPaused && (
                        <Button data-testid={`send-fup-${f.followup_id}`} size="sm" onClick={() => send(f.followup_id)} className="bg-orange-600 hover:bg-orange-700 text-white">
                          <Send className="w-3 h-3 mr-1" /> Senden
                        </Button>
                      )}
                      {!isSent && (
                        <Button
                          data-testid={`toggle-pause-${f.followup_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => togglePause(f.lead_id, isPaused)}
                          title={isPaused ? "Nachfassung fortsetzen" : "Nachfassung pausieren"}
                        >
                          {isPaused ? <><PlayCircle className="w-3 h-3 mr-1" /> Fortsetzen</> : <><PauseCircle className="w-3 h-3 mr-1" /> Pausieren</>}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <p className="px-4 py-8 text-center text-slate-500 text-sm">Wird geladen …</p>
          ) : list.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm mb-3">Keine aktiven Nachfass-Sequenzen.</p>
              <Button data-testid="empty-start-followup-mobile" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                <Plus className="w-4 h-4 mr-2" /> Sequenz starten
              </Button>
            </div>
          ) : list.map((f) => {
            const isPaused = f.status === "paused" || f.paused || f.lead_paused;
            const isSent = f.status === "sent";
            return (
            <div key={f.followup_id} className={`px-4 py-3 ${isPaused && !isSent ? "bg-amber-50/40" : ""}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <button onClick={() => nav(`/leads/${f.lead_id}`)} className="font-semibold text-slate-900 truncate hover:text-orange-600 text-left">
                  {f.customer_name}
                </button>
                <span className="text-sm font-semibold tabular-nums whitespace-nowrap">{formatEUR(f.offer_value)}</span>
              </div>
              <p className="text-xs text-slate-600 truncate">Tag {f.day}: {f.subject}</p>
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${isSent ? "bg-emerald-50 text-emerald-700" : isPaused ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                    {isSent ? "Versendet" : isPaused ? "Pausiert" : "Geplant"}
                  </span>
                  <span className="text-[10px] text-slate-400">{formatDate(f.scheduled_for)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {!isSent && !isPaused && (
                    <Button data-testid={`send-fup-mobile-${f.followup_id}`} size="sm" onClick={() => send(f.followup_id)} className="bg-orange-600 hover:bg-orange-700 text-white h-7 px-2 text-xs">
                      <Send className="w-3 h-3 mr-1" /> Senden
                    </Button>
                  )}
                  {!isSent && (
                    <Button data-testid={`toggle-pause-mobile-${f.followup_id}`} size="sm" variant="outline" onClick={() => togglePause(f.lead_id, isPaused)} className="h-7 px-2 text-xs">
                      {isPaused ? <PlayCircle className="w-3 h-3" /> : <PauseCircle className="w-3 h-3" />}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-followup-dialog">
          <DialogHeader>
            <DialogTitle>Für welches Angebot?</DialogTitle>
            <DialogDescription>
              Es erscheinen nur versendete Angebote ohne aktive Nachfass-Sequenz. 5 Nachrichten werden geplant (Tag 2, 5, 9, 14, 21).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2 space-y-2">
            {eligibleOffers.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-500">
                <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                Aktuell kein passendes Angebot. Versende zuerst ein Angebot, dann kannst du hier eine Nachfass-Sequenz starten.
                <div className="mt-4">
                  <Button data-testid="dialog-go-offers" onClick={() => { setOpen(false); nav("/offers"); }} variant="outline" size="sm">
                    Zu den Angeboten
                  </Button>
                </div>
              </div>
            ) : eligibleOffers.map((o) => (
              <button
                key={o.offer_id}
                data-testid={`pick-offer-${o.offer_id}`}
                onClick={() => startSequence(o.lead_id)}
                disabled={!!busy}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50/40 transition-colors text-left disabled:opacity-50"
              >
                <div>
                  <p className="font-semibold text-slate-900">{o.customer_name}</p>
                  <p className="text-xs text-slate-500">{formatEUR(o.total_price)} · versendet {formatDate(o.sent_at)}</p>
                </div>
                {busy === o.lead_id ? <Loader2 className="w-4 h-4 animate-spin text-orange-600" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
